import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import Slider from 'react-slick'
import Component from 'decorators/Component'
import Div from 'components/Div'
import CarouselGlobalStyle from './Carousel.style'
import { DotButtonContainer, DotButton, PrevButton, NextButton } from './Elements'

const Container = styled(Div)`
  position: relative;
  height: 100%;
`

const SliderContainer = styled(Div)`
  ${props => props.padding ? `padding: ${props.padding};` : ''}
`
SliderContainer.propTypes = {
  padding: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ])
}

class Carousel extends React.PureComponent {
  static propTypes = {
    innerRef: PropTypes.func, // forwarded ref
    dots: PropTypes.bool,
    arrows: PropTypes.bool,
    NextArrow: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    PrevArrow: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    Dot: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    lazyLoad: PropTypes.bool,
    autoplay: PropTypes.bool,
    autoplaySpeed: PropTypes.number, // in ms
    draggable: PropTypes.bool,
    swipe: PropTypes.bool,
    slidesToShow: PropTypes.number,
    slidesToScroll: PropTypes.number,
    framePadding: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
    infinite: PropTypes.bool,
    asNavFor: PropTypes.object,
    beforeChange: PropTypes.func
  }

  static defaultProps = {
    infinite: true,
    dots: true,
    lazyLoad: true,
    arrows: true,
    useTransform: false, // NOTE: There's weird flashing behaviour when slide changes if this's set to true (by default)
    NextArrow: NextButton,
    PrevArrow: PrevButton,
    Dot: DotButton,
    slidesToShow: 1,
    slidesToScroll: 1
  }

  state = {
    slideIndex: 0
  }

  static getDerivedStateFromProps (nextProps, prevState) {
    const slideCount = React.Children.count(nextProps.children)
    if (!prevState || slideCount !== prevState.slideCount) {
      return {
        slideCount
      }
    }
    return null
  }

  setSlideIndex = (current, next) => {
    const { beforeChange } = this.props
    if (typeof beforeChange === 'function') {
      beforeChange(current, next)
    }

    this.setState({ slideIndex: next }, () => {
      // WORKAROUND: Buggy asNavFor in react-slick (https://github.com/akiran/react-slick/issues/1434)
      // TODO: Remove this workaround once the issue is resolved by react-slick
      const { asNavFor } = this.props
      // NOTE: The code below is copied and modified from react-slick
      // (https://github.com/akiran/react-slick/blob/c8d24263517273ae86b982cf783cd4e05ec0be08/src/inner-slider.js#L393)
      if (asNavFor && asNavFor.innerSlider && asNavFor.innerSlider.state.currentSlide !== next) {
        asNavFor.innerSlider.slideHandler(next)
      }
    })
  }

  goToSlide = (index) => this.slider && this.slider.slickGoTo(index)

  goToNextSlide = () => this.slider && this.slider.slickNext()

  goToPrevSlide = () => this.slider && this.slider.slickPrev()

  setCarouselRef = (el) => {
    this.slider = el
    const { innerRef } = this.props
    typeof innerRef === 'function' && innerRef(el)
  }

  // NOTE: Used to render dot controls
  getIndexes = (count, inc) => {
    const arr = []
    for (let i = 0; i < count; i += inc) {
      arr.push(i)
    }
    return arr
  }

  renderDotControls = () => {
    const { t, dots, slidesToScroll, Dot } = this.props
    const { slideCount, slideIndex } = this.state
    if (dots) {
      const indexes = this.getIndexes(slideCount, slidesToScroll)
      return (
        <DotButtonContainer>
          {indexes.map(
            (index) => <Dot ariaLabel={t('Slideshow Dot Button')} key={index} active={index === slideIndex} onClick={() => this.goToSlide(index)} />
          )}
        </DotButtonContainer>
      )
    }
    return null
  }

  renderArrows = () => {
    const { arrows, infinite, PrevArrow, NextArrow, slidesToShow } = this.props
    const { slideCount, slideIndex } = this.state
    if (arrows) {
      return (
        <React.Fragment>
          <PrevArrow onClick={this.goToPrevSlide} disabled={!infinite && slideIndex === 0} />
          <NextArrow onClick={this.goToNextSlide} disabled={!infinite && slideIndex + slidesToShow >= slideCount} />
        </React.Fragment>
      )
    }
    return null
  }

  render () {
    const { children, innerRef, framePadding, ...props } = this.props
    const { slideCount } = this.state

    return slideCount > 1
      ? (
        <React.Fragment>
          <Container>
            <SliderContainer padding={framePadding}>
              {/* NOTE: We don't rely on slick to render controls, so dots & arrows prop is set to false here. */}
              {/* TODO: asNavFor is set to null as a workaround for bugs in react-slick (see this.setSlideIndex method) */}
              <Slider {...props} ref={this.setCarouselRef} asNavFor={null} arrows={false} dots={false} beforeChange={this.setSlideIndex}>
                {children}
              </Slider>
            </SliderContainer>
            {this.renderDotControls()}
            {this.renderArrows()}
          </Container>
          <CarouselGlobalStyle />
        </React.Fragment>
      )
      : children
  }
}

export default Component()(Carousel)
